import { useCallback, useEffect, useRef, useState } from 'react'
import { defaultTableLayout, TableLayout } from '../layout'
import { tableLayoutRequest } from '../fetch/table-layout'

/** Personal layout updates immediately; serialize saves so an older request cannot win. */
export function useTableLayout(tableId: string) {
  const [state, setState] = useState({ tableId, layout: defaultTableLayout, loaded: false, saving: false, error: '' })
  const [attempt, setAttempt] = useState(0)
  const session = useRef({ tableId, layout: defaultTableLayout, revision: 0, active: false, queue: Promise.resolve() })
  useEffect(() => {
    const current = { tableId, layout: defaultTableLayout, revision: 0, active: true, queue: Promise.resolve() }
    session.current = current
    const controller = new AbortController()
    setState({ tableId, layout: defaultTableLayout, loaded: false, saving: false, error: '' })
    void tableLayoutRequest(tableId, undefined, controller.signal)
      .then((layout) => {
        if (!current.active) return
        current.layout = layout
        setState({ tableId, layout, loaded: true, saving: false, error: '' })
      })
      .catch((error: unknown) => {
        if (current.active) setState((s) => ({ ...s, error: error instanceof Error ? error.message : 'Unable to load layout.' }))
      })
    return () => {
      current.active = false
      controller.abort()
    }
  }, [tableId, attempt])
  const change = useCallback((update: (layout: TableLayout) => TableLayout) => {
    const current = session.current
    if (!current.active) return
    const layout = update(current.layout)
    current.layout = layout
    const revision = ++current.revision
    setState((s) => ({ ...s, layout, saving: true, error: '' }))
    current.queue = current.queue.then(async () => {
      try {
        const saved = await tableLayoutRequest(current.tableId, layout)
        if (current.active && current.revision === revision) {
          current.layout = saved
          setState((s) => ({ ...s, layout: saved, saving: false, error: '' }))
        }
      } catch {
        if (current.active && current.revision === revision)
          setState((s) => ({ ...s, saving: false, error: 'Your layout could not be saved. Retry to remember these changes.' }))
      }
    })
  }, [])
  const loaded = state.tableId === tableId && state.loaded
  return {
    layout: loaded ? state.layout : defaultTableLayout,
    loaded,
    saving: state.saving,
    error: state.error,
    change,
    retry: () => (loaded ? change((layout) => layout) : setAttempt((value) => value + 1)),
  }
}
